import { Box, Skeleton, Typography } from '@mui/material';
import { ImageBox } from '@tix-ticketing/ui-components';
import { pinToMinFontSize } from '@tix-ticketing/ui-theme';
import { DiscountedPrice } from 'components/common/price/discounted-price';
import { useServerTimezone } from 'lib/hooks/use-server-timezone';

interface Props {
  eventName: string;
  eventSubtitle: string;
  eventStartsAt: string;
  eventImageUrl: string;
  loading?: boolean;
  /** Hall name shown below the date (subscriptions). */
  hallName?: string;
  /** Per-event total, shown on the date row. */
  eventTotal?: number;
  /** Pre-discount total. Only shown (struck through) when it differs from `eventTotal`. */
  originalEventTotal?: number;
}

export const CartAsideEventCardBody = ({
  eventName,
  eventSubtitle,
  eventStartsAt,
  eventImageUrl,
  loading,
  hallName,
  eventTotal,
  originalEventTotal,
}: Props) => {
  const { formatDate, formatTime } = useServerTimezone();

  if (!eventName || loading)
    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box>
          <Skeleton variant="formItem" width={64} height={64} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Skeleton variant="text" width={80} height={28} />
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}
    >
      <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
        <ImageBox
          imageUrl={eventImageUrl}
          // Decorative: the event name sits beside it.
          altText=""
          variant="thumbnail"
          sx={(theme) => ({
            boxSizing: 'border-box',
            flexShrink: 0,
            width: '64px',
            height: '64px',
            bgcolor: theme.palette.monochrome.main,
          })}
        />
        <Box
          sx={{
            flexGrow: 1,
            pr: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={(theme) => ({
              fontSize: pinToMinFontSize(theme.typography.h6.fontSize),
            })}
          >
            {eventName}
          </Typography>
          {eventSubtitle && (
            <Typography variant="body3" sx={{ color: 'text.secondary' }}>
              {eventSubtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {/* Row 2: date + hall on the left, price on the right. */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {formatDate(eventStartsAt, {
              year: 'numeric',
              month: 'long',
              day: '2-digit',
            })}
            <Box
              component="span"
              sx={{
                width: 4,
                height: 4,
                mx: 1,
                borderRadius: '50%',
                bgcolor: 'text.secondary',
                flexShrink: 0,
              }}
            />
            {formatTime(eventStartsAt, {
              timeStyle: 'short',
            })}
          </Typography>
          {hallName && (
            <Typography variant="body3" sx={{ color: 'text.secondary' }}>
              {hallName}
            </Typography>
          )}
        </Box>
        {eventTotal !== undefined && (
          <DiscountedPrice
            price={eventTotal}
            originalPrice={originalEventTotal}
            variant="body2Bold"
          />
        )}
      </Box>
    </Box>
  );
};
